import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Space, Spin, Radio, Checkbox, Divider, Tag } from 'antd';
import api from '../../utils/api';
import styles from './QuestionDetail.module.css';

const { Title, Text, Paragraph } = Typography;

const QuestionDetail = () => {
  const { collection, id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [error, setError] = useState(null);
  const [userAnswer, setUserAnswer] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchQuestionDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/study/questions/${collection}/${id}`);
      
      if (response.data.success) {
        setQuestion(response.data.data);
      } else {
        throw new Error(response.data.message || '获取题目详情失败');
      }
    } catch (err) {
      console.error('获取题目详情失败:', err);
      setError('获取题目详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionDetail();
  }, [collection, id]);

  const handleOptionClick = (optionLabel) => {
    if (!isSubmitted) {
      setUserAnswer(optionLabel);
      setIsSubmitted(true);
    }
  };

  const isCorrect = userAnswer && (
    Array.isArray(question.answer) 
      ? question.answer.includes(userAnswer)
      : question.answer === userAnswer
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Card className={styles.card}>
          <Title level={4} className={styles.text}>{error}</Title>
          <Button onClick={() => navigate(-1)}>返回</Button>
        </Card>
      </div>
    );
  }

  if (!question) {
    return (
      <div className={styles.errorContainer}>
        <Card className={styles.card}>
          <Title level={4} className={styles.text}>题目不存在或已被删除</Title>
          <Button onClick={() => navigate(-1)}>返回</Button>
        </Card>
      </div>
    );
  }

  // 修改选项处理逻辑
  const options = Array.isArray(question.options) 
    ? question.options 
    : (typeof question.options === 'object' && question.options !== null
      ? Object.entries(question.options).map(([key, value]) => ({ label: key, text: value }))
      : []);

  return (
    <div className={styles.container}>
      <Card className={styles.card} bordered={false}>
        <div className={styles.examTitleContainer}>
          <div className={styles.examTitle}>
            {question.exam_data || '未知试题'}
            <span className={styles.examCategory}>{question.category || '未分类'}</span>
          </div>
          <Button 
            onClick={() => window.close()} 
            className={styles.closeButton}
          >
            关闭
          </Button>
        </div>

        <div className={styles.section}>
          <Title level={5} className={styles.sectionTitle}>题目</Title>
          <div className={styles.questionText}>{question.question || question.questionText}</div>
        </div>

        {/* 选项 */}
        {options && options.length > 0 && (
          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>选项</Title>
            <div className={styles.optionsContainer}>
              {options.map((option, index) => {
                const optionLabel = option.label || String.fromCharCode(65 + index);
                const isSelected = userAnswer === optionLabel;
                const isCorrectOption = isSubmitted && (
                  Array.isArray(question.answer) 
                    ? question.answer.includes(optionLabel)
                    : question.answer === optionLabel
                );
                
                let optionClassName = styles.option;
                if (isSelected) optionClassName += ` ${styles.optionSelected}`;
                if (isCorrectOption) optionClassName += ` ${styles.optionCorrect}`;
                if (isSelected && !isCorrect) optionClassName += ` ${styles.optionWrong}`;
                
                return (
                  <div 
                    key={index}
                    onClick={() => handleOptionClick(optionLabel)}
                    className={optionClassName}
                    style={{ cursor: isSubmitted ? 'default' : 'pointer' }}
                  >
                    <div className={styles.text}>
                      <Text strong>{optionLabel}.</Text> {option.text || option}
                    </div>
                    {isSubmitted && isCorrectOption && (
                      <Text type="success" strong>✓</Text>
                    )}
                    {isSubmitted && isSelected && !isCorrect && (
                      <Text type="danger" strong>✗</Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 答案和解析 - 只在用户提交后显示 */}
        {isSubmitted && (
          <>
            <div className={styles.section}>
              <Title level={4} className={styles.sectionTitle}>答案</Title>
              <div className={styles.answerContainer}>
                <div className={styles.correctAnswer}>
                  <div className={styles.correctAnswerIcon}>
                    {isCorrect ? "✓" : "✗"}
                  </div>
                  <div className={styles.correctAnswerContent}>
                    {isCorrect ? "回答正确！" : "回答错误！"} 
                    正确答案: {Array.isArray(question.answer) ? question.answer.join('、') : question.answer}
                  </div>
                </div>
              </div>
            </div>

            {question.explanation && (
              <div className={styles.section}>
                <Title level={4} className={styles.sectionTitle}>解析</Title>
                <div className={styles.explanation}>
                  <div className={styles.explanationContent}>{question.explanation}</div>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default QuestionDetail; 